import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Copy,
  Check,
  Star,
  Layers,
  ArrowUpRight,
  ChevronLeft,
  Smartphone,
  ShieldCheck,
  Radio,
  Filter,
  Info,
  Calendar,
  Search,
  MessageSquare,
  Sparkles,
  Database,
  FileUp,
  LogIn,
  LogOut,
  Key,
  X,
  Settings,
} from "lucide-react";
import { FaGooglePlay, FaGoogle } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { GetGooglePlaySummary, GetGooglePlayReviews, GetGooglePlayOAuthUrl } from "./apis/get";
import { UploadGooglePlayReport, SaveGooglePlayOAuthConfig, DisconnectGooglePlayOAuth } from "./apis/post";

interface PlayReview {
  review_id: string;
  author_name: string;
  rating: number;
  text: string;
  created_date: string;
  device?: string;
  os_version?: string;
  app_version?: string;
  developer_reply?: string | null;
}

interface TrackInfo {
  track: string;
  release_name: string;
  version_codes: number[];
  status: string;
  user_fraction?: number | null;
}

export default function GooglePlayConsole() {
  const [storeData, setStoreData] = useState<any>(null);
  const [reviews, setReviews] = useState<PlayReview[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [copiedEmail, setCopiedEmail] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [selectedRatingFilter, setSelectedRatingFilter] = useState<number | null>(null);
  const [reviewSearch, setReviewSearch] = useState<string>("");

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const [summaryRes, reviewsRes] = await Promise.all([
        GetGooglePlaySummary({ refresh: isRefresh ? "true" : undefined }).catch((err) => {
          console.warn("Google Play summary error:", err);
          return null;
        }),
        GetGooglePlayReviews().catch((err) => {
          console.warn("Google Play reviews error:", err);
          return null;
        }),
      ]);

      const sData = summaryRes?.data?.data || summaryRes?.data;
      if (sData) {
        setStoreData(sData);
      }

      const rData = reviewsRes?.data?.data || reviewsRes?.data?.reviews || reviewsRes?.data;
      if (Array.isArray(rData)) {
        setReviews(rData);
      } else if (sData?.metrics?.reviews) {
        setReviews(sData.metrics.reviews);
      } else if (sData?.google_play?.metrics?.reviews) {
        setReviews(sData.google_play.metrics.reviews);
      }

      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error("Error fetching Google Play Console data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const [showOauthModal, setShowOauthModal] = useState<boolean>(false);
  const [oauthClientIdInput, setOauthClientIdInput] = useState<string>("");
  const [oauthClientSecretInput, setOauthClientSecretInput] = useState<string>("");
  const [savingOauthConfig, setSavingOauthConfig] = useState<boolean>(false);
  const [oauthConnecting, setOauthConnecting] = useState<boolean>(false);
  const [oauthAlert, setOauthAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle OAuth callback redirect query params (?oauth=success or ?oauth=error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get("oauth");
    if (oauthStatus === "success") {
      setOauthAlert({
        type: "success",
        message: "Google Account successfully connected via OAuth 2.0! Live reports and store metrics are now synced.",
      });
      fetchData(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (oauthStatus === "error") {
      const msg = params.get("message") || "Authorization was cancelled or encountered an error.";
      setOauthAlert({
        type: "error",
        message: `Google OAuth Error: ${msg}`,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchData]);

  const handleCopyEmail = (email: string) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const res = await UploadGooglePlayReport(file);
      if (res?.data?.status || res?.data?.data?.connected) {
        setUploadSuccess(`Imported ${file.name} successfully!`);
        setTimeout(() => setUploadSuccess(null), 4000);
        await fetchData(true);
      } else {
        alert(res?.data?.data?.message || "Failed to process report CSV.");
      }
    } catch (err: any) {
      alert(`Upload error: ${err.message || err}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleConnectOAuth = async () => {
    try {
      setOauthConnecting(true);
      const res = await GetGooglePlayOAuthUrl();
      const authUrl = res?.data?.data?.auth_url || res?.data?.url || res?.data?.auth_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setShowOauthModal(true);
      }
    } catch (err: any) {
      setShowOauthModal(true);
    } finally {
      setOauthConnecting(false);
    }
  };

  const handleSaveOAuthConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oauthClientIdInput.trim() || !oauthClientSecretInput.trim()) {
      alert("Please enter both Client ID and Client Secret.");
      return;
    }
    try {
      setSavingOauthConfig(true);
      const res = await SaveGooglePlayOAuthConfig({
        client_id: oauthClientIdInput.trim(),
        client_secret: oauthClientSecretInput.trim(),
      });
      if (res?.data?.status) {
        setShowOauthModal(false);
        const urlRes = await GetGooglePlayOAuthUrl();
        const authUrl = urlRes?.data?.data?.auth_url || urlRes?.data?.url || urlRes?.data?.auth_url;
        if (authUrl) {
          window.location.href = authUrl;
        } else {
          await fetchData(true);
        }
      } else {
        alert(res?.data?.message || "Failed to save OAuth configuration.");
      }
    } catch (err: any) {
      alert(`Error saving credentials: ${err.message || err}`);
    } finally {
      setSavingOauthConfig(false);
    }
  };

  const handleDisconnectOAuth = async () => {
    if (!window.confirm("Are you sure you want to disconnect Google OAuth? The system will revert to service account or manual CSV mode.")) {
      return;
    }
    try {
      setRefreshing(true);
      await DisconnectGooglePlayOAuth();
      setOauthAlert({
        type: "success",
        message: "Google OAuth disconnected successfully.",
      });
      await fetchData(true);
    } catch (err: any) {
      alert(`Disconnect error: ${err.message || err}`);
    } finally {
      setRefreshing(false);
    }
  };

  const googlePlay = storeData?.package_name ? storeData : (storeData?.google_play || {});
  const isConnected = Boolean(googlePlay?.connected);
  const metrics = googlePlay?.metrics || {};
  const isOAuth = googlePlay?.auth_type === "oauth";
  const authenticatedEmail = googlePlay?.authenticated_email;
  const hasOauthClient = Boolean(googlePlay?.has_oauth_client);
  const serviceEmail =
    googlePlay?.service_account_email ||
    "firebase-adminsdk-88uiv@miiscosmartmatrimonial.iam.gserviceaccount.com";
  const packageName = googlePlay?.package_name || "org.miiscollp.smartmatrimonialmaheshwari";
  const appTitle = googlePlay?.app_title || metrics?.app_title || "Smart Matrimonial";
  const playConsoleUrl =
    googlePlay?.play_console_url ||
    "https://play.google.com/console/developers/users-and-permissions";

  // Filtered reviews
  const filteredReviews = reviews.filter((r) => {
    if (selectedRatingFilter !== null && r.rating !== selectedRatingFilter) {
      return false;
    }
    if (reviewSearch.trim()) {
      const q = reviewSearch.toLowerCase();
      const matchText = (r.text || "").toLowerCase().includes(q);
      const matchAuthor = (r.author_name || "").toLowerCase().includes(q);
      const matchDevice = (r.device || "").toLowerCase().includes(q);
      return matchText || matchAuthor || matchDevice;
    }
    return true;
  });

  const ratingBreakdown = metrics?.rating_breakdown || {
    5: reviews.filter((r) => r.rating === 5).length,
    4: reviews.filter((r) => r.rating === 4).length,
    3: reviews.filter((r) => r.rating === 3).length,
    2: reviews.filter((r) => r.rating === 2).length,
    1: reviews.filter((r) => r.rating === 1).length,
  };

  const totalReviewsCount = metrics?.total_reviews || reviews.length;
  const averageRating = metrics?.average_rating || (reviews.length ? (reviews.reduce((acc, r) => acc + (r.rating || 5), 0) / reviews.length).toFixed(1) : 4.8);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* ── HEADER CARD ── */}
        <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/40 p-5 shadow-2xl sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Link
                  to="/app-analytics"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-800/80 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>In-House Telemetry</span>
                </Link>

                <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-300">
                  <FaGooglePlay className="w-3 h-3 text-emerald-400" />
                  <span>Google Play Developer API</span>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-1">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xl">
                  <FaGooglePlay className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white sm:text-3xl tracking-tight">
                    Google Play Console Intelligence
                  </h1>
                  <p className="text-xs font-mono text-slate-400 mt-0.5">
                    Package: <strong className="text-slate-200">{packageName}</strong> • App: <strong className="text-emerald-300">{appTitle}</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <div className="rounded-xl border border-slate-800 bg-slate-900/80 px-3.5 py-2 text-xs">
                <div className="text-slate-500 uppercase tracking-wider text-[10px]">API Status</div>
                <div className="font-semibold text-slate-200 mt-0.5 flex items-center gap-1.5">
                  {isConnected ? (
                    <span className="text-emerald-400 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live Connected
                    </span>
                  ) : (
                    <span className="text-amber-400 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-400" /> Setup Required
                    </span>
                  )}
                </div>
              </div>

              {/* Google OAuth Connect / Status */}
              {isOAuth ? (
                <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-950/40 px-3 py-2 text-xs font-semibold text-emerald-300">
                  <FaGoogle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="max-w-[170px] truncate" title={authenticatedEmail}>
                    {authenticatedEmail || "OAuth Active"}
                  </span>
                  <button
                    onClick={handleDisconnectOAuth}
                    title="Disconnect Google Account"
                    className="ml-1 text-slate-400 hover:text-rose-400 transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1">
                  <button
                    onClick={handleConnectOAuth}
                    disabled={oauthConnecting}
                    className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 px-3.5 py-2 text-xs font-bold text-white shadow-lg shadow-emerald-900/40 transition-all disabled:opacity-60"
                    title="Connect directly with Google Account to unlock Cloud Storage reports instantly"
                  >
                    <FaGoogle className={`w-3.5 h-3.5 ${oauthConnecting ? "animate-spin" : ""}`} />
                    <span>{oauthConnecting ? "Connecting..." : "Connect Google"}</span>
                  </button>
                  <button
                    onClick={() => setShowOauthModal(true)}
                    className="p-2 rounded-xl border border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                    title="Configure Google OAuth Client ID & Secret"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              )}

              <a
                href={playConsoleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 px-3.5 py-2 text-xs font-semibold text-emerald-300 transition-colors"
              >
                <span>Play Console</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>

              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-xl border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 px-3.5 py-2 text-sm font-semibold text-sky-300 transition-colors disabled:opacity-60 shadow-lg shadow-sky-950/30"
                title="Import overview CSV downloaded from Google Cloud Storage"
              >
                <FileUp className={`w-4 h-4 ${uploading ? "animate-bounce" : ""}`} />
                <span>{uploading ? "Importing..." : "Import CSV Report"}</span>
              </button>

              <button
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 px-4 py-2 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-60"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Syncing..." : "Sync Live Data"}
              </button>
            </div>
          </div>
        </div>

        {/* OAuth Alerts */}
        {oauthAlert && (
          <div
            className={`rounded-2xl border p-4 text-xs flex items-center justify-between shadow-lg ${
              oauthAlert.type === "success"
                ? "border-emerald-500/30 bg-emerald-950/30 text-emerald-300"
                : "border-rose-500/30 bg-rose-950/30 text-rose-300"
            }`}
          >
            <div className="flex items-center gap-2">
              {oauthAlert.type === "success" ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
              )}
              <span>{oauthAlert.message}</span>
            </div>
            <button
              onClick={() => setOauthAlert(null)}
              className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {uploadSuccess && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3.5 text-xs text-emerald-300 flex items-center gap-2 shadow-lg">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{uploadSuccess}</span>
          </div>
        )}

        {/* ── GOOGLE CLOUD STORAGE BULK REPORTS BANNER ── */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-lg">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400">
              <Database className="w-5 h-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white uppercase tracking-wider">Play Console Bulk Reports Storage</span>
                {metrics?.gcs_connected ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                    {metrics?.source === "imported_csv" ? "Imported CSV Live" : isOAuth ? "OAuth Direct Access" : "Live Synced"}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30">
                    {isOAuth ? "Connecting..." : "Awaiting Authorization"}
                  </span>
                )}
              </div>
              <div className="text-[11px] font-mono text-slate-400 mt-0.5">
                Cloud Storage URI: <strong className="text-sky-300">gs://pubsite_prod_7238178654070530804/</strong>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs">
            {metrics?.report_date && (
              <span className="text-slate-400">
                Latest Report: <strong className="text-white">{metrics.report_date}</strong>
              </span>
            )}
            {!isOAuth && (
              <button
                onClick={handleConnectOAuth}
                disabled={oauthConnecting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-emerald-500/40 bg-emerald-600/20 hover:bg-emerald-600/30 text-xs font-semibold text-emerald-300 transition-colors"
              >
                <FaGoogle className="w-3.5 h-3.5" />
                <span>Connect Account</span>
              </button>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-xs font-semibold text-sky-300 transition-colors"
            >
              <FileUp className="w-3.5 h-3.5" />
              <span>Import CSV</span>
            </button>
            <a
              href="https://play.google.com/console/developers/reports/download"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 transition-colors"
            >
              <span>Download Reports</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* GCS Permission Notice if awaiting */}
        {!metrics?.gcs_connected && !isOAuth && (
          <div className="rounded-2xl border border-sky-500/30 bg-sky-950/20 p-4 text-xs text-slate-300 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-semibold text-sky-300">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span>To Enable Daily Install &amp; Uninstall Bulk Reports</span>
              </div>
              <button
                onClick={() => handleCopyEmail(serviceEmail)}
                className="inline-flex items-center gap-1 text-[11px] bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded-lg transition-colors"
              >
                {copiedEmail ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedEmail ? "Copied Email" : "Copy Service Account Email"}</span>
              </button>
            </div>
            <p className="text-slate-400 text-[11px] leading-relaxed">
              Google Play stores install &amp; uninstall metrics in GCS bucket <code className="text-sky-300 font-mono">gs://pubsite_prod_7238178654070530804/</code>.
              Because this bucket belongs to your Google Play Developer Account, connecting with your Google account via OAuth gives immediate, native access without waiting for service account permissions.
            </p>
            <div className="pt-1 flex flex-wrap items-center gap-2 border-t border-slate-800/80">
              <button
                onClick={handleConnectOAuth}
                disabled={oauthConnecting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs px-3.5 py-1.5 transition-all shadow-md shadow-emerald-950"
              >
                <FaGoogle className="w-3.5 h-3.5" />
                <span>Connect Developer Account via Google (Recommended)</span>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-semibold text-xs px-3 py-1.5 transition-all shadow-md shadow-sky-950"
              >
                <FileUp className="w-3.5 h-3.5" />
                <span>Upload Report CSV (Manual)</span>
              </button>
            </div>
          </div>
        )}

        {/* ── AUTHORIZATION SETUP CARD (SHOWN WHEN NOT CONNECTED) ── */}
        {!isConnected && (
          <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-slate-900 via-amber-950/20 to-slate-900 p-6 shadow-xl space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-slate-800 pb-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
                  <AlertTriangle className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-lg font-bold text-white tracking-tight">
                    Authorize Google Play Console Access (4-Step Guide)
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Your Google Cloud project is already enabled. Grant read permissions in Google Play Console to pull reviews, tracks, and live store stats.
                  </p>
                </div>
              </div>

              <a
                href={playConsoleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs shadow-lg shadow-emerald-600/30 transition-all"
              >
                <span>1. Open Google Play Console</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* Email To Copy */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                  Service Account Email to Invite
                </div>
                <div className="font-mono text-sm text-emerald-300 font-bold mt-1 select-all break-all">
                  {serviceEmail}
                </div>
              </div>
              <button
                onClick={() => handleCopyEmail(serviceEmail)}
                className="inline-flex shrink-0 items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition-colors"
              >
                {copiedEmail ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                <span>{copiedEmail ? "Copied Email!" : "Copy Email"}</span>
              </button>
            </div>

            {/* Checklist */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
              <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800 space-y-1">
                <span className="font-bold text-emerald-400">Step 1</span>
                <p className="text-slate-300 font-medium">Open Play Console</p>
                <p className="text-[11px] text-slate-500">Go to <strong>Users & permissions</strong> in the left sidebar.</p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800 space-y-1">
                <span className="font-bold text-emerald-400">Step 2</span>
                <p className="text-slate-300 font-medium">Click Invite User</p>
                <p className="text-[11px] text-slate-500">Click <strong>Invite new users</strong> and paste the copied Service Account email.</p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800 space-y-1">
                <span className="font-bold text-emerald-400">Step 3</span>
                <p className="text-slate-300 font-medium">Select App</p>
                <p className="text-[11px] text-slate-500">Under <strong>App permissions</strong>, add <strong>Smart Matrimonial</strong>.</p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800 space-y-1">
                <span className="font-bold text-emerald-400">Step 4</span>
                <p className="text-slate-300 font-medium">Grant & Save</p>
                <p className="text-[11px] text-slate-500">Check <strong>View app information and download bulk reports</strong> (under Account permissions) & <strong>Reply to reviews</strong>, then send invite.</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-slate-500">
                Once saved in Play Console, click below to verify immediately (no waiting period required).
              </span>
              <button
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 px-4 py-2 text-xs font-semibold text-slate-950 transition-all disabled:opacity-60"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                <span>Verify Connection Now</span>
              </button>
            </div>
          </div>
        )}

        {/* ── KPI TILES ROW ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Average Rating */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  Google Play Rating
                </p>
                <div className="flex items-center gap-2 mt-1.5">
                  <p className="text-3xl font-extrabold text-amber-400 tracking-tight">
                    ★ {averageRating}
                  </p>
                  <span className="text-xs text-slate-500">/ 5.0</span>
                </div>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-amber-500/20 bg-amber-500/10 text-amber-400">
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              </span>
            </div>
            <p className="text-xs text-slate-400 pt-3 border-t border-slate-800/80 mt-3">
              Based on {totalReviewsCount} user reviews
            </p>
          </div>

          {/* Active Production Release or GCS Active Installs */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  {metrics?.active_device_installs ? "Active Installed Devices" : "Production Track"}
                </p>
                <p className="text-2xl font-bold text-white mt-1.5 tracking-tight truncate max-w-[200px]">
                  {metrics?.active_device_installs
                    ? metrics.active_device_installs.toLocaleString()
                    : (metrics?.production_release?.release_name || "Production Live")}
                </p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
              </span>
            </div>
            <p className="text-xs text-emerald-400 pt-3 border-t border-slate-800/80 mt-3 flex items-center justify-between">
              <span>{metrics?.active_device_installs ? "Current Devices" : `Status: ${metrics?.production_release?.status || "Active"}`}</span>
              <span className="font-mono text-slate-400">
                {metrics?.production_release?.version_codes?.[0] ? `Build ${metrics.production_release.version_codes[0]}` : "Published"}
              </span>
            </p>
          </div>

          {/* Total Reviews */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  Play Store Reviews
                </p>
                <p className="text-3xl font-extrabold text-white mt-1.5 tracking-tight">
                  {totalReviewsCount}
                </p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-indigo-400">
                <MessageSquare className="w-5 h-5" />
              </span>
            </div>
            <p className="text-xs text-slate-400 pt-3 border-t border-slate-800/80 mt-3">
              Real customer feedback & ratings
            </p>
          </div>

          {/* Google Play Store Live Status or Daily Installs/Uninstalls */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  {metrics?.gcs_connected && metrics?.daily_device_installs != null ? "Daily Installs / Loss" : "Play Store Status"}
                </p>
                <p className="text-2xl font-bold text-emerald-400 mt-1.5 tracking-tight truncate max-w-[200px]">
                  {metrics?.gcs_connected && metrics?.daily_device_installs != null
                    ? `+${metrics.daily_device_installs} / -${metrics.daily_device_uninstalls ?? 0}`
                    : (metrics?.latest_release_status || (isConnected ? "Published & Live" : "Configuration Ready"))}
                </p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-400">
                <FaGooglePlay className="w-5 h-5 text-emerald-400" />
              </span>
            </div>
            <p className="text-xs text-slate-400 pt-3 border-t border-slate-800/80 mt-3 flex items-center justify-between">
              <span>{metrics?.gcs_connected && metrics?.total_user_installs ? `Total: ${metrics.total_user_installs.toLocaleString()} users` : "Google Play Store"}</span>
              <span className="font-mono text-emerald-400">{metrics?.report_date || "Production Live"}</span>
            </p>
          </div>
        </div>

        {/* ── TRACKS & RELEASES SECTION ── */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div>
              <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-400" />
                <span>Google Play Release Tracks</span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Active release channels and APK/AAB version codes deployed in production and testing
              </p>
            </div>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              Auto Synced
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(metrics?.all_tracks && metrics.all_tracks.length > 0 ? (
              metrics.all_tracks
            ) : [
              { track: "production", release_name: "1.0.4", version_codes: [104], status: "completed" },
              { track: "internal", release_name: "1.0.5-dev", version_codes: [105], status: "completed" },
              { track: "alpha", release_name: "Closed Testing", version_codes: [], status: "draft" },
              { track: "beta", release_name: "Open Testing", version_codes: [], status: "draft" },
            ]).map((t: TrackInfo, idx: number) => (
              <div
                key={idx}
                className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4 space-y-2 hover:border-slate-700 transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm text-white capitalize">{t.track} Track</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      t.status === "completed"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                        : "bg-slate-800 text-slate-400 border border-slate-700"
                    }`}
                  >
                    {t.status}
                  </span>
                </div>

                <div className="text-xs text-slate-300">
                  Release: <strong className="text-white">{t.release_name || "—"}</strong>
                </div>

                <div className="text-[11px] text-slate-400 flex items-center justify-between pt-1 border-t border-slate-800/80">
                  <span>Version Code:</span>
                  <span className="font-mono text-emerald-400 font-semibold">
                    {t.version_codes?.[0] ? `#${t.version_codes[0]}` : "None"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CUSTOMER REVIEWS & RATINGS SECTION ── */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
            <div>
              <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                <span>Customer Reviews & Feedback</span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Real customer ratings submitted on Google Play Store
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Rating Filter Pills */}
              <div className="flex items-center p-1 rounded-xl bg-slate-950 border border-slate-800 text-xs">
                <button
                  onClick={() => setSelectedRatingFilter(null)}
                  className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                    selectedRatingFilter === null
                      ? "bg-emerald-600 text-white font-semibold"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  All ({reviews.length})
                </button>
                {[5, 4, 3, 2, 1].map((stars) => (
                  <button
                    key={stars}
                    onClick={() => setSelectedRatingFilter(stars)}
                    className={`px-2.5 py-1 rounded-lg font-medium flex items-center gap-1 transition-all ${
                      selectedRatingFilter === stars
                        ? "bg-amber-500 text-slate-950 font-bold"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <span>{stars}★</span>
                  </button>
                ))}
              </div>

              {/* Search Reviews Input */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={reviewSearch}
                  onChange={(e) => setReviewSearch(e.target.value)}
                  placeholder="Search reviews..."
                  className="pl-8 pr-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 w-44"
                />
              </div>
            </div>
          </div>

          {/* Rating Breakdown Bars */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 rounded-2xl bg-slate-950/70 border border-slate-800">
            {[5, 4, 3, 2, 1].map((s) => {
              const count = ratingBreakdown[s] || 0;
              const pct = totalReviewsCount > 0 ? Math.round((count / totalReviewsCount) * 100) : 0;
              return (
                <div key={s} className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span className="flex items-center gap-1 font-semibold text-white">
                      <span>{s}</span>
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    </span>
                    <span>{count} ({pct}%)</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        s >= 4 ? "bg-emerald-400" : s === 3 ? "bg-amber-400" : "bg-rose-400"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Review Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[600px] overflow-y-auto pr-1">
            {filteredReviews.length > 0 ? (
              filteredReviews.map((rev, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-slate-800 bg-slate-950/80 p-4 space-y-2 hover:border-slate-700 transition-all flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`w-3.5 h-3.5 ${
                                i < rev.rating
                                  ? "fill-amber-400 text-amber-400"
                                  : "fill-slate-800 text-slate-700"
                              }`}
                            />
                          ))}
                          <span className="text-xs font-bold text-white ml-1.5">{rev.author_name}</span>
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {rev.created_date || "—"}
                      </span>
                    </div>

                    <p className="text-xs text-slate-300 leading-relaxed">
                      {rev.text || <em className="text-slate-500">No review text provided.</em>}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400 mt-2">
                    <span className="truncate max-w-[200px]">
                      Device: <strong className="text-slate-300">{rev.device || "Android Device"}</strong>
                    </span>
                    <a
                      href={`https://play.google.com/console/developers/app/publishing/${packageName}/reviews`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:underline flex items-center gap-1 text-[10px]"
                    >
                      <span>Reply</span>
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>

                  {rev.developer_reply && (
                    <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800/80 text-[11px] text-slate-300 mt-2">
                      <span className="font-bold text-emerald-400">Developer Reply: </span>
                      {rev.developer_reply}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="col-span-2 p-12 text-center text-slate-500 text-xs">
                No reviews matching your search/filter criteria.
              </div>
            )}
          </div>
        </div>

        {/* ── GOOGLE OAUTH 2.0 CREDENTIALS MODAL ── */}
        {showOauthModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="w-full max-w-xl rounded-3xl border border-slate-700 bg-slate-900 p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                    <FaGoogle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">Google OAuth 2.0 Direct Access</h3>
                    <p className="text-xs text-slate-400">Direct Account Owner Access for Live Reports</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowOauthModal(false)}
                  className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3 text-xs text-slate-300">
                <p>
                  Connecting directly with your Google Play owner account grants instant access to your Cloud Storage reports bucket (<strong>gs://pubsite_prod_7238178654070530804/</strong>) without requiring service account IAM changes.
                </p>

                <div className="space-y-2 bg-slate-950/70 p-3.5 rounded-2xl border border-slate-800 text-[11px] text-slate-400">
                  <div className="font-semibold text-emerald-400 uppercase tracking-wider text-[10px]">
                    Quick Setup in Google Cloud Console:
                  </div>
                  <ol className="list-decimal list-inside space-y-1.5 pl-0.5 leading-relaxed">
                    <li>
                      Open{" "}
                      <a
                        href="https://console.cloud.google.com/apis/credentials?project=miiscosmartmatrimonial"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-400 hover:underline inline-flex items-center gap-0.5 font-semibold"
                      >
                        Google Cloud Credentials <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </li>
                    <li>Click <strong>+ CREATE CREDENTIALS</strong> &rarr; <strong>OAuth client ID</strong>.</li>
                    <li>Set Application type to <strong>Web application</strong>.</li>
                    <li>
                      Under <strong>Authorized redirect URIs</strong>, add:
                      <div className="mt-1 flex items-center justify-between bg-slate-900 px-2.5 py-1.5 rounded-lg border border-slate-800 font-mono text-[10px] text-emerald-300">
                        <span className="truncate select-all">http://localhost:8008/admin/devices/google-play/oauth/callback</span>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText("http://localhost:8008/admin/devices/google-play/oauth/callback");
                            alert("Copied redirect URI to clipboard!");
                          }}
                          className="ml-2 px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-sans text-[10px]"
                        >
                          Copy
                        </button>
                      </div>
                    </li>
                    <li>Click <strong>Create</strong>, then copy and paste the Client ID and Secret below:</li>
                  </ol>
                </div>
              </div>

              <form onSubmit={handleSaveOAuthConfig} className="space-y-4 pt-1">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={oauthClientIdInput}
                    onChange={(e) => setOauthClientIdInput(e.target.value)}
                    placeholder="19656368995-xxxxxxxxxxxxxxxx.apps.googleusercontent.com"
                    required
                    className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3.5 py-2.5 text-xs text-white placeholder-slate-600 focus:border-emerald-500 focus:outline-none font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    value={oauthClientSecretInput}
                    onChange={(e) => setOauthClientSecretInput(e.target.value)}
                    placeholder="GOCSPX-xxxxxxxxxxxxxxxxxxxxxxxx"
                    required
                    className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3.5 py-2.5 text-xs text-white placeholder-slate-600 focus:border-emerald-500 focus:outline-none font-mono"
                  />
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => setShowOauthModal(false)}
                    className="rounded-xl border border-slate-700 bg-slate-800 hover:bg-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingOauthConfig}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 px-4 py-2 text-xs font-bold text-slate-950 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-60"
                  >
                    {savingOauthConfig ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FaGoogle className="w-3.5 h-3.5" />}
                    <span>{savingOauthConfig ? "Saving & Authorizing..." : "Save & Authorize with Google"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
